/**
 * 로그인 연동 현황의 화면 라벨 (#219)
 *
 * 코드 값(상태 · 사유)의 정본은 백엔드다 — services/oidc/integrations.py · authorization.py.
 * 여기는 그 값을 사람이 읽는 말로 옮기기만 한다. 모르는 값이 오면 코드를 그대로 보여 준다.
 */
import type { BadgeVariant } from '@/components/ui/Badge';
import type { IntegrationStatus } from '@/services/apis/serviceClientAPI';

export const INTEGRATION_STATUS: Record<IntegrationStatus, { label: string; variant: BadgeVariant; hint: string }> = {
  healthy: { label: '정상', variant: 'success', hint: '로그인이 끝까지 진행된 기록이 있고 지금 실패하고 있지 않습니다.' },
  failing: { label: '실패 중', variant: 'error', hint: '마지막 로그인 성공 이후 실패가 있습니다. 실패 사유를 확인해 주세요.' },
  no_traffic: { label: '기록 없음', variant: 'warning', hint: '클라이언트를 발급했지만 로그인 기록이 없습니다. 서비스에서 로그인을 한 번 테스트해 주세요.' },
  not_issued: { label: '미발급', variant: 'default', hint: '로그인 연동 전입니다. 서비스 관리에서 클라이언트를 발급해 주세요.' },
  revoked: { label: '폐기됨', variant: 'inactive', hint: '클라이언트를 폐기해 이 서비스로는 로그인할 수 없습니다.' },
};

/** 목록 위 요약에 보이는 순서. 손이 필요한 것부터 */
export const INTEGRATION_STATUS_ORDER: IntegrationStatus[] = [
  'failing', 'no_traffic', 'healthy', 'not_issued', 'revoked',
];

export const INTEGRATION_STEP_LABELS: Record<string, string> = {
  authorize: '인가',
  token: '토큰 교환',
};

/** 실패 사유 → 원인과 확인할 곳 */
export const INTEGRATION_FAILURE_REASONS: Record<string, string> = {
  client_revoked: '폐기된 클라이언트로 요청했습니다. 서비스의 client_id 설정을 새 값으로 바꿔 주세요.',
  redirect_not_registered: '콜백 주소가 등록된 주소와 다릅니다. 끝의 /까지 같은지 확인해 주세요.',
  resource_not_registered: '등록되지 않은 resource를 요청했습니다.',
  response_type_unsupported: 'response_type이 code가 아닙니다.',
  grant_type_unsupported: 'grant_type이 authorization_code가 아닙니다.',
  pkce_method_unsupported: 'PKCE 방식이 S256이 아닙니다.',
  pkce_verifier_missing: '인가 때 PKCE를 썼는데 토큰 교환에 code_verifier가 없습니다.',
  pkce_failed: 'code_verifier가 인가 때의 값과 맞지 않습니다. 서비스의 세션 저장을 확인해 주세요.',
  client_auth_missing: 'client_secret과 code_verifier가 모두 없습니다.',
  client_secret_invalid: 'client_secret이 맞지 않습니다. 재발급했다면 서비스 설정을 바꿔 주세요.',
  code_invalid: '존재하지 않는 코드입니다.',
  code_reused: '이미 사용한 코드를 다시 보냈습니다. 서비스가 같은 코드를 두 번 보내지 않는지 확인해 주세요.',
  code_expired: '코드가 만료됐습니다(60초). 서비스가 코드를 바로 교환하는지 확인해 주세요.',
  code_client_mismatch: '다른 클라이언트가 발급받은 코드입니다.',
  code_redirect_mismatch: '토큰 교환의 redirect_uri가 인가 때와 다릅니다.',
  code_resource_mismatch: '토큰 교환의 resource가 인가 때와 다릅니다.',
  user_missing: '코드를 받은 사용자가 삭제됐습니다.',
  user_inactive: '코드를 받은 뒤 사용자가 정지되었거나 승인이 취소되었습니다.',
  unclassified: '분류되지 않은 실패입니다. 백엔드 로그를 확인해 주세요.',
};

export const INTEGRATION_PERIODS = [1, 7, 30, 90] as const;
